import { useEffect, useRef, useState } from 'react';
import { ShieldCheck, X } from '@phosphor-icons/react';
import { cn } from '@concord-platform/ui/cn';
import { Button } from './Button';
import { Switch } from './Switch';

export type ConsentBannerProps = {
  acceptButtonText: string;
  bannerLinkText: string;
  bannerLayout: 'bar' | 'box';
  bannerPosition:
    | 'top'
    | 'bottom'
    | 'center'
    | 'left_bottom'
    | 'left_top'
    | 'right_bottom'
    | 'right_top';
  bannerTheme: 'brand' | 'light' | 'dark';
  bannerTitleEnabled: boolean;
  bannerTitleText: string;
  bannerText: string;
  className?: string;
  closeButtonEnabled: boolean;
  customizeButtonEnabled: boolean;
  customizeButtonText: string;
  denyButtonEnabled: boolean;
  denyButtonText: string;
  doNotSellEnabled: boolean;
  doNotSellText: string;
  gcmEnabled: boolean;
  gcmLinkText: string;
  gcmPolicyUrl: string;
  gpcSignal: boolean;
  gpcText: string;
  onCustomizeClick?: () => void;
  onFinishClick?: (options: { acceptAll: boolean; doNotSell: boolean }) => void;
  policyUrl?: string | null;
  privacyCenterOpen: boolean;
  // for accessibility - param is optional as we don't need this in admin-ui preview
  shiftFocusOnLoad?: boolean;
  zIndex?: number;
};

export function ConsentBannerUI({
  acceptButtonText,
  bannerLayout,
  bannerLinkText,
  bannerPosition,
  bannerTheme,
  bannerTitleEnabled,
  bannerTitleText,
  bannerText,
  className,
  closeButtonEnabled,
  customizeButtonEnabled,
  customizeButtonText,
  denyButtonEnabled,
  denyButtonText,
  doNotSellEnabled,
  doNotSellText,
  gpcSignal,
  gpcText,
  gcmEnabled,
  gcmLinkText,
  gcmPolicyUrl,
  onCustomizeClick,
  onFinishClick,
  policyUrl,
  privacyCenterOpen,
  shiftFocusOnLoad,
  zIndex,
}: ConsentBannerProps) {
  const acceptButtonRef = useRef<HTMLButtonElement>(null);
  const [doNotSell, setDoNotSell] = useState(gpcSignal);

  let transformClass = '';
  let positionClass = '';
  let layoutClass = '';

  if (bannerLayout === 'bar') {
    if (privacyCenterOpen) {
      transformClass =
        bannerPosition === 'top'
          ? '-translate-y-full transition-transform'
          : 'translate-y-full transition-transform';
    }
    positionClass =
      bannerPosition === 'top'
        ? 'top-0 left-0 right-0 border-b border-cb-border animate-in slide-in-from-top duration-300'
        : 'bottom-0 left-0 right-0 border-t border-cb-border animate-in slide-in-from-bottom duration-300';
    layoutClass = 'w-full';
  } else if (bannerLayout === 'box') {
    transformClass = privacyCenterOpen
      ? 'opacity-0 transition-opacity'
      : 'opacity-100 transition-opacity';
    layoutClass =
      'max-w-[90%] max-h-[90%] md:w-[448px] lg:w-[512px] xl:w-[576px] h-fit overflow-auto rounded-card border border-cb-border';
    switch (bannerPosition) {
      case 'center':
        positionClass = 'inset-0 m-auto animate-in fade-in duration-300';
        break;
      case 'left_bottom':
        positionClass = 'bottom-0 left-0 m-6 animate-in fade-in duration-300';
        break;
      case 'left_top':
        positionClass = 'top-0 left-0 m-6 animate-in fade-in duration-300';
        break;
      case 'right_bottom':
        positionClass = 'bottom-0 right-0 m-6 animate-in fade-in duration-300';
        break;
      case 'right_top':
        positionClass = 'top-0 right-0 m-6 animate-in fade-in duration-300';
        break;
    }
  }

  const baseClasses = cn(
    'fixed text-header text-sm font-medium leading-relaxed shadow-lg overflow-auto max-h-[100vh]',
    transformClass,
    positionClass,
    layoutClass,
  );

  const themeClasses = {
    light: 'light bg-background text-foreground',
    dark: 'dark bg-background text-foreground',
    brand: 'brand bg-secondary text-secondary-foreground',
  };

  /**
   * Shift focus to accept button on load for screen readers - use this element
   * as it cannot be turned off
   */
  useEffect(() => {
    if (shiftFocusOnLoad && acceptButtonRef.current) {
      acceptButtonRef.current.focus();
    }
  }, []);

  return (
    <div
      className={cn(baseClasses, themeClasses[bannerTheme], className)}
      style={{
        zIndex,
      }}
      tabIndex={-1}
    >
      <div className="relative p-6">
        <div
          className={cn(
            'flex flex-col justify-between gap-6',
            bannerLayout === 'bar' && 'xl:flex-row',
          )}
        >
          <div className="flex flex-col space-y-4">
            {bannerTitleEnabled && (
              <h2
                className={cn('text-lg font-bold')}
                data-testid="cd-banner-title"
              >
                {bannerTitleText}
              </h2>
            )}
            <p className="space-x-2">
              <span>{bannerText}</span>
              <a
                className={cn('text-link font-bold underline')}
                href={policyUrl ? policyUrl : undefined}
              >
                {bannerLinkText}
              </a>
              {gcmEnabled && gcmLinkText && gcmPolicyUrl && (
                <>
                  <a
                    className={cn('text-link font-bold underline')}
                    href={gcmPolicyUrl}
                  >
                    {gcmLinkText}
                  </a>
                </>
              )}
            </p>
            {gpcSignal && (
              <div
                className={cn(
                  'border-primary bg-primary-transparent rounded-button w-fit border p-2',
                )}
              >
                <div className="flex items-center space-x-2">
                  <ShieldCheck className="text-primary h-6 w-6" />
                  <p className="text-sm">{gpcText}</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col justify-center space-y-4">
            {doNotSellEnabled && (
              <div className="flex max-w-full items-center space-x-2">
                <Switch
                  checked={doNotSell}
                  data-testid="cd-do-not-sell-switch"
                  onCheckedChange={() =>
                    setDoNotSell((prevState) => !prevState)
                  }
                />
                <p>{doNotSellText}</p>
              </div>
            )}
            <div className="flex flex-col-reverse items-center gap-4 sm:flex-row sm:gap-4">
              {customizeButtonEnabled && (
                <Button
                  aria-label="Customize"
                  className="w-full min-w-32"
                  data-testid="cd-customize-button"
                  size="lg"
                  variant="cb-outline"
                  onClick={onCustomizeClick}
                >
                  {customizeButtonText}
                </Button>
              )}
              {denyButtonEnabled && (
                <Button
                  aria-label="Deny All"
                  className="w-full min-w-32"
                  data-testid="cd-deny-all-button"
                  size="lg"
                  variant="primary"
                  onClick={() =>
                    onFinishClick &&
                    onFinishClick({ acceptAll: false, doNotSell })
                  }
                >
                  {denyButtonText}
                </Button>
              )}
              <Button
                ref={acceptButtonRef}
                aria-label="Accept All"
                className="w-full min-w-32"
                data-testid="cd-accept-all-button"
                size="lg"
                variant="primary"
                onClick={() =>
                  onFinishClick && onFinishClick({ acceptAll: true, doNotSell })
                }
              >
                {acceptButtonText}
              </Button>
            </div>
          </div>
        </div>
        {closeButtonEnabled && (
          <Button
            aria-label="Close"
            className={cn(
              'absolute right-3 top-3 opacity-80 transition-opacity hover:opacity-100',
              bannerTheme !== 'brand' && 'hover:text-primary text-foreground',
            )}
            size="icon"
            variant="link"
            onClick={() =>
              onFinishClick && onFinishClick({ acceptAll: false, doNotSell })
            }
          >
            <X />
          </Button>
        )}
      </div>
    </div>
  );
}
